import React, { useState, useEffect } from 'react';
import { Sparkles, Star, Gem, X, Clock, Check, Copy, Users, CheckCircle, Award, Camera, Zap, CreditCard, Lock, AlertCircle, Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppHeader from './shared/AppHeader';
import PackageCard from './shared/PackageCard';
import { apiClient } from '../utils/apiClient';
import { API_URL } from '../config';
import { formatSnaps, snapText } from '../utils/snapFormatter';

export default function CreditsPageSNAP() {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [userSnaps, setUserSnaps] = useState(0);
  const [hasActiveAvatar, setHasActiveAvatar] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [showReferral, setShowReferral] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatingNewLink, setGeneratingNewLink] = useState(false);
  const [showLinkToast, setShowLinkToast] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Custom Amount State
  const [customAmount, setCustomAmount] = useState(10);
  
  // Pakete dynamisch aus DB laden
  const [packages, setPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  
  // Bonus Settings dynamisch laden
  const [bonusSettings, setBonusSettings] = useState(null);
  
  // Pricing Settings für Preisliste
  const [pricingSettings, setPricingSettings] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);

  // Price List Items (NEU - dynamisch)
  const [priceListItems, setPriceListItems] = useState([]);
  
  // Referral Texte dynamisch laden
  const [referralTexts, setReferralTexts] = useState(null);

  // Get current user ID
  const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
  const userId = user.id;
  const isMasterAdmin = user.email === 'info@snapshotpro.ai';
  
  // Dynamische Minimum-Berechnung basierend auf snaps_per_euro
  const MIN_EUR = 10;  // Minimum Kaufbetrag in EUR
  const snapsPerEuro = pricingSettings?.snaps_per_euro || 2;
  const minSnaps = MIN_EUR * snapsPerEuro;
  const minEurDisplay = MIN_EUR.toFixed(2);
  
  useEffect(() => {
    fetchPackages();
    fetchBonusSettings();
    fetchPricingSettings();
    fetchPriceListItems();  // NEU
    fetchReferralTexts();   // NEU: Referral-Texte laden

    // Auto-refresh Pricing Settings alle 10s
    const interval = setInterval(() => {
      fetchPricingSettings();
      fetchPriceListItems();  // NEU
      fetchReferralTexts();   // NEU
    }, 10000);
    return () => clearInterval(interval);

  }, []);
  
  const fetchPricingSettings = async () => {
    try {
      const res = await apiClient.get('/api/pricing/settings/public');
      if (res.ok) {
        const data = await res.json();
        setPricingSettings(data);
      }
    } catch (error) {
      console.error('Error fetching pricing settings:', error);
    } finally {
      setLoadingPricing(false);
    }
  };
  
  const fetchReferralTexts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/referral/texts/public`);
      if (res.ok) {
        const data = await res.json();
        setReferralTexts(data);
      }
    } catch (error) {
      console.error('Error fetching referral texts:', error);
      // Fallback auf Default-Texte
      setReferralTexts({
        header_title: "SNAPs",
        header_subtitle: "Verwalte deine SNAPs und kaufe neue Pakete",
        before_spending_title: "Bevor du Geld ausgibst... 💡",
        before_spending_text: "Schnapp dir kostenlose SNAPs, indem du deinen persönlichen Einladungslink teilst.",
        main_title: "Freunde einladen & SNAPs verdienen",
        main_subtitle: "Du erhältst SNAPs einmalig pro neuem Freund, wenn dieser erfolgreich ein Paket kauft.",
        wichtig_text: "Wichtig: Belohnungen werden einmalig pro neuem Freund vergeben.",
        how_it_works_title: "So funktioniert's",
        step1_title: "Link teilen",
        step1_text: "Teile deinen persönlichen Einladungslink mit Freunden.",
        step2_title: "Freund kauft ein Paket",
        step2_text: "Dein Freund erstellt seinen Avatar oder kauft SNAPs.",
        step3_title: "SNAPs verdienen (einmalig!)",
        step3_text: "Du erhältst einmalig Bonus-SNAPs pro gekauftem Paket",
        packages_title: "SNAP Pakete",
        packages_subtitle: "Nutze SNAPs für Avatar-Training, Bilder, Remixes & Vehicles"
      });
    }
  };
  
  const fetchPriceListItems = async () => {
    try {
      const res = await apiClient.get('/api/pricing/price-list/public');
      if (res.ok) {
        const data = await res.json();
        setPriceListItems(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching price list items:', error);
    }
  };
  
  const fetchPackages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pricing/packages`);
      if (res.ok) {
        const data = await res.json();
        
        if (data.packages && data.packages.length > 0) {
          // Map API packages zu Frontend-Format
          const mapped = data.packages.map((pkg, idx) => ({
            id: pkg.id,
            name: pkg.name,
            icon: idx === 0 ? Sparkles : idx === 1 ? Star : Gem,
            price: pkg.price_eur,
            avatarCount: pkg.avatar_count || 0,      // NEU: avatarCount statt snaps
            imageCount: pkg.image_count || 0,        // NEU: imageCount statt images
            description: pkg.description,
            subtext: pkg.subtext || '',
            isTraining: pkg.avatar_count > 0,
            isPopular: pkg.is_popular || false,
            useGradient: pkg.use_gradient || false,  // NEU: Farbiger Hintergrund
            // Design-Felder
            iconType: pkg.icon_type || 'sparkles',
            template: pkg.template || 'classic',
            accentColor: pkg.accent_color || '#10B981',
            customBadge: pkg.custom_badge
          }));
          setPackages(mapped);
        } else {
          console.warn('Keine Pakete gefunden!');
        }
      } else {
        console.error('Fehler beim Laden der Pakete:', res.statusText);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoadingPackages(false);
    }
  };
  
  const avatarPackages = packages.filter(pkg => pkg.isTraining);
  const snapPackages = [];

  // Fetch user SNAPs
  const fetchSnaps = async () => {
    try {
      const res = await apiClient.get('/api/user/credits');
      if (res.ok) {
        const data = await res.json();
        setUserSnaps(data.credits || 0);
      }
    } catch (error) {
      console.error('Error fetching SNAPs:', error);
    }
  };


  // Fetch bonus settings
  const fetchBonusSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/referral/bonus-info`);
      if (res.ok) {
        const data = await res.json();
        setBonusSettings(data);
      }
    } catch (error) {
      console.error('Error fetching bonus settings:', error);
    }
  };


  // Fetch referral stats
  const fetchReferralStats = async () => {
    try {
      const res = await apiClient.get('/api/referral/stats');
      if (res.ok) {
        const data = await res.json();
        setReferralStats(data);
      }
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    }
  };

  // Fetch transaction history
  const fetchHistory = async () => {
    try {
      const res = await apiClient.get('/api/user/transaction-history');
      if (res.ok) {
        const data = await res.json();
        setTransactionHistory(data.transactions || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Check if user has active avatar
  const checkActiveAvatar = async () => {
    try {
      const res = await apiClient.get('/api/user/avatar-status');
      const data = await res.json();
      setHasActiveAvatar(data.has_avatar && data.can_generate);
    } catch (error) {
      console.error('Error checking avatar status:', error);
      setHasActiveAvatar(false);
    }
  };

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      console.log('Payment status polling timed out');
      return;
    }

    try {
      const res = await apiClient.get(`/api/stripe/checkout-status/${sessionId}`);
      const data = await res.json();
      
      if (data.payment_status === 'paid') {
        // Success!
        await fetchSnaps();
        await fetchHistory();
        await fetchReferralStats();
        setShowSuccessMessage(true);
        
        // Auto-redirect back to wizard to start training after 2 seconds
        setTimeout(() => {
          window.location.href = '/wizard?step=2'; // Direkt zu Step 2 (PersonalData)
        }, 2000);
        return;
      } else if (data.status === 'expired') {
        console.log('Payment session expired');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error polling payment:', error);
    }
  };

  // Check for return from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    
    if (sessionId && success === 'true') {
      pollPaymentStatus(sessionId);
    }
    
    fetchSnaps();
    fetchReferralStats();
    fetchHistory();
    checkActiveAvatar();

  }, []);

  // Early return if no user ID
  if (!userId) {
    console.error('No user ID found');
    return null;
  }

  const handleBuyClick = (pkg) => {
    // Keine Avatar-Sperre mehr! User kann immer kaufen
    setSelectedPackage(pkg);
    setShowModal(true);
  };


  const handleCustomAmountBuy = async () => {
    if (customAmount < minSnaps) {
      alert(`Mindestens ${minSnaps} SNAPs erforderlich (= €${minEurDisplay})`);
      return;
    }
    if (customAmount > 500) {
      alert('Maximum 500 SNAPs pro Kauf');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const originUrl = window.location.origin;
      
      const res = await apiClient.post('/api/stripe/checkout-session-custom', {
        amount_snaps: customAmount,
        origin_url: originUrl
      });

      if (!res.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await res.json();
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Fehler beim Erstellen der Checkout-Session. Bitte versuche es erneut.');
      setIsProcessing(false);
    }
  };


  const handleConfirmPurchase = async () => {
    if (!selectedPackage) return;
    
    setIsProcessing(true);
    
    try {
      const originUrl = window.location.origin;
      
      const res = await apiClient.post('/api/stripe/checkout-session', {
        package_id: selectedPackage.id,
        origin_url: originUrl
      });

      if (!res.ok) {
        throw new Error('Failed to create checkout session');
      }

      const data = await res.json();
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Fehler beim Erstellen der Checkout-Session. Bitte versuche es erneut.');
      setIsProcessing(false);
    }
  };

  const handleCopyLink = () => {
    if (referralStats && referralStats.referral_code) {
      const link = `https://www.snapshotpro.ai/auth?ref=${referralStats.referral_code}`;
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGenerateNewLink = async () => {
    setGeneratingNewLink(true);
    try {
      const res = await apiClient.post('/api/referral/generate-new-link', {});
      
      if (!res.ok) {
        throw new Error('Request failed');
      }
      
      const data = await res.json();
      setReferralStats(data);
      
      // NEU: Lade Stats nochmal frisch vom Server
      await fetchReferralStats();
      
      // Show toast instead of alert
      setShowLinkToast(true);
      setTimeout(() => setShowLinkToast(false), 3000);
    } catch (error) {
      console.error('Error generating new link:', error);
      alert('Fehler beim Generieren eines neuen Links');
    } finally {
      setGeneratingNewLink(false);
    }
  };

  const handleShare = (platform) => {
    if (!referralStats || !referralStats.referral_code) {
      alert('Referral-Code nicht verfügbar. Bitte lade die Seite neu.');
      return;
    }
    
    // Nutze konfigurierbaren Text aus bonusSettings oder Fallback
    const shareText = bonusSettings?.referral_share_text || 'Erstelle deine eigenen KI-Avatare mit SNAPSHOT! 🎨\n\nA product of snapshotpro.ai';
    const link = `https://www.snapshotpro.ai/auth?ref=${referralStats.referral_code}`;
    
    const urls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n\n' + link)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`
    };
    
    if (urls[platform]) {
      window.open(urls[platform], '_blank');
    }
  };

  // BEIDE Paket-Typen gleichzeitig anzeigen (nicht switchen!)
  // Avatar-Pakete: IMMER kaufbar
  // SNAP-Pakete: Nur mit aktivem Avatar kaufbar

  return (
    <div className="min-h-screen bg-black grain-texture text-white">
      
      {/* Shared Header Component */}
      <AppHeader menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(!menuOpen)} />
      
      {/* Overlay - Schließt Menü bei Klick außerhalb */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}
      
      {/* Side Menu - SCROLLBAR FÜR LANGE LISTEN */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col p-8 space-y-6 mt-20 h-full overflow-y-auto pb-24">
          
          {/* User Info im Menü */}
          <div className="pb-4 border-b border-white/10 mb-2">
            <p className="text-sm text-white/50 mb-1">{user.name || 'User'}</p>
            <p className="text-xs text-white/40 mb-3">{user.email}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#10B981] font-medium">{Math.round(userSnaps * 100) / 100} SNAPs</span>
              <span className="text-xs text-white/60">Du bist hier</span>
            </div>
          </div>
          
          <Link 
            to="/"
            className="text-white hover:text-[#10B981] transition-colors text-lg font-medium"
            onClick={() => setMenuOpen(false)}
          >
            🏠 Meine ID Card
          </Link>
          
          {/* Master Admin Link */}
          {isMasterAdmin && (
            <Link 
              to="/admin" 
              className="text-[#10B981] hover:text-[#059669] transition-colors text-lg font-bold flex items-center gap-2 border-t border-b border-white/10 py-4 -mx-2 px-2"
              onClick={() => setMenuOpen(false)}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Admin-Panel
            </Link>
          )}
          
          <button 
            onClick={() => {
              setMenuOpen(false);
              localStorage.removeItem('snapshot_token');
              localStorage.removeItem('snapshot_user');
              localStorage.removeItem('snapshot_beta_access');
              window.location.href = '/welcome';
            }}
            className="text-white/60 hover:text-white transition-colors text-sm text-left"
          >
            🚪 Abmelden
          </button>
          <div className="border-t border-white/10 my-2"></div>
          <Link to="/was-ist-snapshot" className="text-white/60 hover:text-white transition-colors text-sm" onClick={() => setMenuOpen(false)}>
            Was ist SNAPSHOT
          </Link>
          <Link to="/impressum" className="text-white/60 hover:text-white transition-colors text-sm" onClick={() => setMenuOpen(false)}>
            Impressum
          </Link>
          <Link to="/datenschutz" className="text-white/60 hover:text-white transition-colors text-sm" onClick={() => setMenuOpen(false)}>
            Datenschutz
          </Link>
          <Link to="/agb" className="text-white/60 hover:text-white transition-colors text-sm" onClick={() => setMenuOpen(false)}>
            AGB
          </Link>
          <Link to="/kontakt" className="text-white/60 hover:text-white transition-colors text-sm" onClick={() => setMenuOpen(false)}>
            Kontakt
          </Link>
          <button onClick={() => window.dispatchEvent(new Event('openCookieSettings'))} className="text-white/60 hover:text-white transition-colors text-sm text-left">
            Cookie-Einstellungen
          </button>
        </nav>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-12 sm:pb-16">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="text-white">{referralTexts?.header_title || 'SNAPs'}</span>
          </h1>
          <p className="text-white/60 text-lg">
            {referralTexts?.header_subtitle || 'Verwalte deine SNAPs und kaufe neue Pakete'}
          </p>
        </div>

        {/* Current SNAPs Info */}
        <div className="max-w-2xl mx-auto mb-12 p-6 bg-black border-2 border-[#10B981]/30 rounded-2xl glow-green-soft">
          <div className="text-center">
            <p className="text-white/70 text-sm mb-2">Verfügbare SNAPs</p>
            <p className="text-6xl font-bold text-[#10B981] mb-2">{Math.round(userSnaps * 100) / 100}</p>
            <p className="text-white/60 text-sm">
              = €{(userSnaps / snapsPerEuro).toFixed(2)} Guthaben
            </p>
          </div>
        </div>

        {/* Cool CTA - HIER ZWISCHEN GUTHABEN UND REFERRAL */}
        <div className="max-w-3xl mx-auto mb-12 text-center">
          <p className="text-2xl font-bold text-white mb-2">
            {referralTexts?.before_spending_title || 'Bevor du Geld ausgibst... 💡'}
          </p>
          <p className="text-white/70 text-lg leading-relaxed whitespace-pre-wrap">
            {referralTexts?.before_spending_text || 'Schnapp dir kostenlose SNAPs, indem du deinen persönlichen Einladungslink teilst.'}
          </p>
        </div>

        {/* Referral Section */}
        {referralStats && (
          <div className="max-w-4xl mx-auto mb-12 p-6 sm:p-8 border-2 border-[#10B981]/30 rounded-2xl bg-[#10B981]/5">
            
            <div className="text-center mb-8">
              <h3 className="text-2xl sm:text-3xl font-bold mb-3">
                {referralTexts?.main_title || 'Freunde einladen & SNAPs verdienen'}
              </h3>
              <p className="text-white/70 leading-relaxed whitespace-pre-wrap">
                {referralTexts?.main_subtitle || 'Du erhältst SNAPs einmalig pro neuem Freund, wenn dieser erfolgreich ein Paket kauft.'}
              </p>
            </div>

            {/* Stats - MIT 5 BOXEN */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-center">
                <Users className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{referralStats.total_invites || 0}</p>
                <p className="text-sm text-white/60">Freunde eingeladen</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-center">
                <CheckCircle className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{referralStats.successful_registrations || 0}</p>
                <p className="text-sm text-white/60">Erfolgreiche Registrierungen</p>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-xl text-center">
                <CreditCard className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                <p className="text-3xl font-bold text-white">{referralStats.successful_purchases || 0}</p>
                <p className="text-sm text-white/60">Erfolgreiche Käufe</p>
              </div>
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-center">
                <Gift className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-3xl font-bold text-emerald-400">{referralStats?.free_images || 0}</p>
                <p className="text-sm text-emerald-300/80">Kostenlose Bilder</p>
                <p className="text-xs text-emerald-400/60 mt-1">Verdient durch Referrals: {referralStats?.referral_images_earned || 0}</p>
              </div>
            </div>
            
            {/* Snapshot-Geschenke Text (unter Kacheln) - NETTO-Berechnung */}
            {(() => {
              const gifted = transactionHistory.filter(tx => tx.type === 'admin_gift_images').reduce((sum, tx) => sum + (tx.amount_images || 0), 0);
              const deducted = transactionHistory.filter(tx => tx.type === 'admin_deduct_images').reduce((sum, tx) => sum + Math.abs(tx.amount_images || 0), 0);
              const netGifts = gifted - deducted;
              
              // TEMP: Immer anzeigen zum Debuggen (auch wenn 0)
              return (
                <div className="mt-6 mb-6 p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-lg text-center">
                  <p className="text-emerald-400/80 text-sm">
                    💝 Snapshot hat dir bisher <span className="font-bold text-emerald-400">{netGifts}</span> kostenlose Bilder geschenkt
                    {netGifts === 0 && <span className="text-xs text-white/40 ml-2">(Noch keine Geschenke)</span>}
                  </p>
                </div>
              );
            })()}

            {/* Hinweistext unter Zählern */}
            <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl">
              <p className="text-sm text-yellow-400 leading-relaxed whitespace-pre-wrap">
                {referralTexts?.wichtig_text || 'Wichtig: Belohnungen werden einmalig pro neuem Freund vergeben.'}
              </p>
            </div>

            {/* Referral Link */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-white/70">
                  Dein persönlicher Einladungslink
                </label>
                <button
                  onClick={handleGenerateNewLink}
                  disabled={generatingNewLink}
                  className="text-xs text-[#10B981] hover:text-[#059669] font-medium flex items-center gap-1 disabled:opacity-50"
                  data-testid="generate-new-link-button"
                >
                  🔄 Neuen Link generieren
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={referralStats?.referral_code ? `https://www.snapshotpro.ai/auth?ref=${referralStats.referral_code}` : 'Lädt...'}
                  readOnly
                  className="flex-1 px-4 py-3 bg-black border-2 border-white/30 rounded-xl text-white text-sm font-mono"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    copied ? 'bg-[#10B981] text-black' : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                  }`}
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
              
              {/* Link Status */}
              {referralStats.is_active ? (
                <p className="text-xs text-[#10B981] mt-2">✓ Link ist aktiv</p>
              ) : (
                <p className="text-xs text-orange-500 mt-2">⚠️ Dieser Link ist abgelaufen. Bitte generiere einen neuen Link.</p>
              )}
            </div>

            {/* Share Buttons - NUR WhatsApp & Telegram */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-white/70 mb-3">Direkt teilen</label>
              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="py-4 px-6 bg-white/5 border-2 border-white/20 text-white rounded-xl hover:border-[#10B981] hover:bg-white/10 transition-all font-medium flex flex-col items-center gap-2"
                  data-testid="share-whatsapp"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-[#10B981]">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span className="text-sm">WhatsApp</span>
                </button>
                <button
                  onClick={() => handleShare('telegram')}
                  className="py-4 px-6 bg-white/5 border-2 border-white/20 text-white rounded-xl hover:border-[#10B981] hover:bg-white/10 transition-all font-medium flex flex-col items-center gap-2"
                  data-testid="share-telegram"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-[#10B981]">
                    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.223-.548.223l.188-2.85 5.18-4.68c.223-.198-.054-.308-.346-.11l-6.4 4.02-2.76-.918c-.6-.187-.612-.6.125-.89l10.782-4.156c.5-.176.943.112.78.89z"/>
                  </svg>
                  <span className="text-sm">Telegram</span>
                </button>
              </div>
            </div>

            {/* So funktioniert's */}
            <div className="pt-6 border-t border-white/10">
              <h4 className="text-xl font-bold mb-4 text-center">{referralTexts?.how_it_works_title || 'So funktioniert\'s'}</h4>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#10B981] text-black rounded-full flex items-center justify-center font-bold">1</div>
                  <div>
                    <p className="font-bold text-white mb-1">{referralTexts?.step1_title || 'Link teilen'}</p>
                    <p className="text-sm text-white/70 whitespace-pre-wrap">{referralTexts?.step1_text || 'Teile deinen persönlichen Einladungslink mit Freunden, Familie oder auf Social Media.'}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#10B981] text-black rounded-full flex items-center justify-center font-bold">2</div>
                  <div>
                    <p className="font-bold text-white mb-1">{referralTexts?.step2_title || 'Freund kauft ein Paket'}</p>
                    <p className="text-sm text-white/70 whitespace-pre-wrap">{referralTexts?.step2_text || 'Dein Freund erstellt seinen Avatar oder kauft SNAPs.'}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#10B981] text-black rounded-full flex items-center justify-center font-bold">3</div>
                  <div>
                    <p className="font-bold text-white mb-1">{referralTexts?.step3_title || 'SNAPs verdienen (einmalig!)'}</p>
                    <p className="text-sm text-white/70 whitespace-pre-wrap">
                      {referralTexts?.step3_text || 'Du erhältst einmalig Bonus-SNAPs pro gekauftem Paket'}
                      {bonusSettings && packages.length > 0 && (
                        <>: {packages.map((pkg, idx) => {
                          const bonus = bonusSettings.referrer_bonus_per_package?.[pkg.id] || 0;
                          return bonus > 0 ? `${pkg.name} (${bonus} SNAP${bonus > 1 ? 's' : ''})` : null;
                        }).filter(Boolean).join(', ')}</>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SNAP PAKETE - EINFACHES SYSTEM */}
        <div className="mb-16">

          <div>
            <h2 className="text-3xl font-bold text-center mb-4">
              {referralTexts?.packages_title || 'SNAP Pakete'}
            </h2>
            <p className="text-center text-white/60 mb-8">
              {referralTexts?.packages_subtitle || 'Nutze SNAPs für Avatar-Training, Bilder, Remixes & Vehicles'}
            </p>

            {/* Loading Skeleton für Pakete */}
            {(loadingPackages || loadingPricing) ? (
              <div className="mb-12">
                <div className="h-6 w-48 bg-white/10 animate-pulse rounded mx-auto mb-6"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-6 sm:p-8 rounded-2xl border-2 border-white/10 bg-black/50">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/10 animate-pulse"></div>
                      <div className="h-7 w-32 bg-white/10 animate-pulse rounded mx-auto mb-2"></div>
                      <div className="h-4 w-48 bg-white/10 animate-pulse rounded mx-auto mb-4"></div>
                      <div className="h-10 w-28 bg-white/10 animate-pulse rounded mx-auto mb-4"></div>
                      <div className="h-5 w-36 bg-white/10 animate-pulse rounded mx-auto mb-6"></div>
                      <div className="h-12 w-full bg-white/10 animate-pulse rounded-xl"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
            <>
            {/* Empfohlene Pakete */}
            {avatarPackages.length > 0 && (
              <div className="mb-12">
                <h3 className="text-xl font-bold text-center text-white/80 mb-6">Empfohlene Pakete</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {avatarPackages.map((pkg) => (
                    <PackageCard
                      key={pkg.id}
                      pkg={pkg}
                      template={pkg.template}
                      accentColor={pkg.accentColor}
                      onBuyClick={handleBuyClick}
                      previewMode={false}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Freier Betrag - Cool verpackt */}
            <div className="w-full max-w-4xl mx-auto">
              <div className="p-8 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/30 rounded-2xl">
                <h3 className="text-2xl font-bold text-center mb-2">
                  SNAPs kaufen <span className="text-[#10B981]">·</span> Wähle deinen Betrag
                </h3>
                <p className="text-center text-white/60 mb-6 text-sm">
                  Min: {minSnaps} SNAPs (€{minEurDisplay})
                </p>
                
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-bold text-white block mb-2">Anzahl SNAPs</label>
                    <input
                      type="number"
                      min={minSnaps}
                      max="500"
                      step="1"
                      value={customAmount}
                      onChange={(e) => {
                        const val = e.target.value;
                        // Erlaube leeren String (für Editing)
                        if (val === '') {
                          setCustomAmount('');
                          return;
                        }
                        // Parse als Number
                        const num = parseInt(val);
                        if (!isNaN(num) && num >= 0) {
                          setCustomAmount(num);
                        }
                      }}
                      onBlur={(e) => {
                        // Bei Blur: Validiere Minimum
                        const val = parseInt(e.target.value);
                        if (isNaN(val) || val < minSnaps) {
                          setCustomAmount(minSnaps);
                        }
                      }}
                      className="w-full px-4 py-4 bg-black/90 border-2 border-white/20 rounded-xl text-white text-3xl font-bold text-center focus:border-[#10B981] transition-all"
                    />
                    <p className="text-base text-[#10B981] mt-3 font-bold text-center">
                      = €{(customAmount / snapsPerEuro).toFixed(2)}
                    </p>
                    <p className="text-xs text-white/40 text-center mt-1">inkl. 19% MwSt.</p>
                     <button
          onClick={() => handleCustomAmountBuy()}
          disabled={customAmount < minSnaps}
          className="w-full px-10 py-6 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-xl font-bold text-xl hover:from-[#059669] hover:to-[#047857] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-lg hover:shadow-xl glow-green mx-auto mt-6"
        >
          Jetzt kaufen
        </button>
                  </div>
                  
          
                </div>
              </div>
            </div>
            
            {/* Info-Text unter Avatar-Paketen */}
            <div className="w-full max-w-4xl mx-auto">
              <div className="text-center mt-8 p-6 bg-black/50 border-2 border-[#10B981]/30 rounded-2xl">
                <p className="text-sm text-white/70">
                  Nutze deine SNAPs für Avatar-Training, Bilder generieren und Remixes
                </p>
              </div>
            </div>
            </>
            )}
           </div>

          {/* SNAP Packages */}
          <div className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {snapPackages.map((pkg) => {
                const Icon = pkg.icon;
                return (
                  <div
                    key={pkg.id}
                    className={`relative p-6 sm:p-8 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                      pkg.isPopular
                        ? 'border-[#10B981] bg-gradient-to-br from-[#10B981]/10 to-black glow-green'
                        : 'border-white/20 bg-black/50 hover:border-white/40'
                    }`}
                    data-testid={`package-${pkg.id}`}
                  >
                    {pkg.isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full">
                        <p className="text-black font-bold text-xs sm:text-sm">Bestseller</p>
                      </div>
                    )}

                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${
                      pkg.isPopular ? 'bg-gradient-to-br from-[#10B981] to-[#059669] glow-green' : 'bg-white/10'
                    }`}>
                      <Icon className={`w-8 h-8 ${pkg.isPopular ? 'text-black' : 'text-[#10B981]'}`} />
                    </div>

                    <h3 className="text-2xl font-bold text-center mb-2">{pkg.name}</h3>
                    <p className="text-white/60 text-center text-sm mb-4">{pkg.description}</p>

                    <div className="text-center mb-4">
                      <p className="text-4xl font-bold">€{pkg.price.toFixed(2)}</p>
                      <p className="text-xs text-white/40 mt-1">inkl. 19% MwSt.</p>
                    </div>

                    <div className="text-center mb-2">
                      <p className="text-sm text-white/70">Du erhältst</p>
                      <p className="text-3xl font-bold text-[#10B981]">{pkg.snaps} SNAPs</p>
                      <p className="text-sm text-white/50">= {pkg.images} Bilder</p>
                    </div>

                    <div className="text-center mb-6">
                      <p className="text-xs text-white/50">€{pkg.pricePerSnap.toFixed(2)} pro SNAP</p>
                    </div>

                    <button
                      onClick={() => handleBuyClick(pkg)}
                      className={`w-full py-3 px-6 rounded-xl font-bold transition-all ${
                        pkg.isPopular
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:from-yellow-600 hover:to-orange-600'
                          : hasActiveAvatar
                          ? 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                          : 'bg-white/5 text-white/30 cursor-pointer border border-white/10'
                      }`}
                      data-testid={`buy-button-${pkg.id}`}
                    >
                      {hasActiveAvatar ? 'Jetzt kaufen' : (
                        <div className="flex items-center justify-center gap-2">
                          <Lock className="w-4 h-4" />
                          Gesperrt
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
            
          </div>
        </div>
        
        {/* Transparente Preisliste - Gleiches Design wie "SNAPs kaufen" */}

        <div className="w-full max-w-4xl mx-auto">
          {loadingPricing ? (
            <div className="p-8 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/30 rounded-2xl">
              <div className="h-7 w-48 bg-white/10 animate-pulse rounded mx-auto mb-2"></div>
              <div className="h-4 w-56 bg-white/10 animate-pulse rounded mx-auto mb-8"></div>
              <div className="mb-6 p-4 bg-[#10B981]/10 border border-[#10B981]/40 rounded-xl">
                <div className="h-6 w-full bg-white/10 animate-pulse rounded"></div>
              </div>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 bg-white/10 animate-pulse rounded"></div>
                      <div className="h-4 w-32 bg-white/10 animate-pulse rounded"></div>
                    </div>
                    <div className="h-4 w-20 bg-white/10 animate-pulse rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
          <div className="p-8 bg-gradient-to-br from-[#10B981]/10 to-black border-2 border-[#10B981]/30 rounded-2xl">

            {/* Header */}
            <h3 className="text-2xl font-bold text-center mb-2">
              💎 SNAP <span className="text-[#10B981]">Preisliste</span>
            </h3>
            <p className="text-center text-white/60 mb-8 text-sm">
              Alle Preise transparent & fair
            </p>
            
            {/* Conversion-Rate - Prominent & Dynamisch */}
            <div className="mb-6 p-4 bg-[#10B981]/10 border border-[#10B981]/40 rounded-xl">
              <div className="flex items-center justify-between">
                <span className="text-white font-bold">1 Euro</span>
                <div className="flex-1 mx-4 border-t-2 border-dashed border-[#10B981]/30"></div>
                <span className="text-[#10B981] font-bold text-xl">{pricingSettings?.snaps_per_euro || 2} SNAPs</span>
              </div>
            </div>
            
            {/* Preisliste Items - Dynamisch aus Admin-Editor */}
            <div className="space-y-2">
              
              {priceListItems.length > 0 ? (
                priceListItems.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10"
                    style={{ borderColor: `${item.color}20` }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{item.icon}</span>
                      <div>
                        <span className="text-white text-sm">{item.label}</span>
                        {item.description && <p className="text-white/40 text-xs">{item.description}</p>}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="font-bold" style={{ color: item.color }}>
                        {item.snaps} SNAP{item.snaps !== 1 ? 's' : ''}
                      </span>
                      <p className="text-white/40 text-xs"> €{((item.snaps || 1) / (pricingSettings?.snaps_per_euro || 2)).toFixed(2)}</p>
                    </div>
                  </div>
                ))
              ) : (
                /* Fallback: Alte Hardcoded Items (falls Admin noch keine Items erstellt hat) */
                <>
                  {/* Training (universal) */}
                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">🎓</span>
                      <span className="text-white text-sm">Training (Avatar, Vehicle, etc.)</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[#10B981] font-bold">{pricingSettings?.snaps_per_avatar || 50} SNAPs</span>
                      <p className="text-white/40 text-xs"> €{((pricingSettings?.snaps_per_avatar || 50) / (pricingSettings?.snaps_per_euro || 2)).toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Bild (universal) */}
                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">📸</span>
                      <span className="text-white text-sm">1 Bild generieren</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[#10B981] font-bold">{snapText(pricingSettings?.snaps_per_shot || 1)}</span>
                      <p className="text-white/40 text-xs"> €{((pricingSettings?.snaps_per_shot || 1) / (pricingSettings?.snaps_per_euro || 2)).toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Remix (universal) */}
                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">✨</span>
                      <span className="text-white text-sm">1 Remix (Variation)</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[#10B981] font-bold">{pricingSettings?.snaps_per_remix || 1} SNAP</span>
                      <p className="text-white/40 text-xs"> €{((pricingSettings?.snaps_per_remix || 1) / (pricingSettings?.snaps_per_euro || 2)).toFixed(2)}</p>
                    </div>
                  </div>
                  
                  {/* Modify (universal - NEU) */}
                  <div className="flex items-center justify-between p-3 bg-black/40 rounded-xl border border-white/10">
                    <div className="flex items-center gap-3">
                      <span className="text-lg">🎨</span>
                      <span className="text-white text-sm">1 Modify (Details ändern)</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[#10B981] font-bold">{pricingSettings?.snaps_per_modify || 1} SNAP</span>
                      <p className="text-white/40 text-xs"> €{((pricingSettings?.snaps_per_modify || 1) / (pricingSettings?.snaps_per_euro || 2)).toFixed(2)}</p>
                    </div>
                  </div>
                </>
              )}
              
            </div>

          </div>
          )}
        </div>
        <br />

        {/* 30-Tage Expiry Warnung */}
        <div className="max-w-3xl mx-auto mb-12 p-6 bg-orange-500/10 border-2 border-orange-500/30 rounded-xl">
          <div className="flex gap-4 items-start">
            <div className="flex-shrink-0 mt-1">
              <svg className="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="font-bold text-orange-500 mb-2">⚠️ Wichtig: Avatar-Kraft</p>
              <p className="text-sm text-white/70 leading-relaxed">
                Nach 30 Tagen verliert dein Avatar seine Kraft. Aktiviere die <span className="text-[#10B981] font-semibold">Auto-Verlängerung</span> in deiner ID-Card für nur <span className="text-[#10B981] font-semibold">3 SNAPs/Monat</span> – oder erstelle einen neuen Avatar. Deine SNAPs bleiben erhalten!
              </p>
            </div>
          </div>
        </div>

        {/* Transaction History - Kontoauszug Style */}
        <div className="mt-16 max-w-6xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">Transaktionshistorie</h2>
            <p className="text-white/60 text-sm">Vollständige Übersicht aller Kontobewegungen</p>
          </div>

          {loadingHistory ? (
            <div className="text-center py-12 bg-black/30 border border-white/10 rounded-lg">
              <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full mx-auto"></div>
              <p className="text-white/50 mt-4">Lade Transaktionen...</p>
            </div>
          ) : transactionHistory.length === 0 ? (
            <div className="text-center py-12 bg-black/30 border border-white/10 rounded-lg">
              <p className="text-white/50">Noch keine Transaktionen vorhanden</p>
              <p className="text-white/40 text-sm mt-2">Ihre Käufe werden hier aufgelistet</p>
            </div>
          ) : (
            <div className="bg-black/30 border border-white/10 rounded-lg overflow-hidden">
              
              {/* Table Header - Desktop */}
              <div className="hidden md:grid md:grid-cols-12 gap-3 px-6 py-4 bg-white/5 border-b border-white/10 font-semibold text-xs text-white/70 uppercase tracking-wider">
                <div className="col-span-2">Datum</div>
                <div className="col-span-1">Uhrzeit</div>
                <div className="col-span-5">Verwendungszweck</div>
                <div className="col-span-2 text-right">SNAPs</div>
                <div className="col-span-2 text-right">Betrag (EUR)</div>
              </div>

              {/* Table Rows */}
              <div className="divide-y divide-white/10">
                {transactionHistory.map((tx, idx) => {
                  // Backend speichert UTC ohne Timezone-Marker — 'Z' anhängen für korrekte Konvertierung
                  const rawDate = tx.created_at;
                  const dateInput = rawDate && !rawDate.endsWith('Z') && !rawDate.includes('+') ? rawDate + 'Z' : rawDate;
                  const date = new Date(dateInput);
                  const dateStr = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
                  const timeStr = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
                  
                  // Verwendungszweck bestimmen
                  let verwendungszweck = '';
                  const isExpense = tx.amount_snaps < 0;  // NEU: Ausgabe oder Einnahme?
                  
                  if (tx.type === 'usage') {
                    // SNAP-Ausgaben (Vehicle/Avatar Nutzung)
                    verwendungszweck = tx.description || tx.reason || 'SNAP-Ausgabe';
                  } else if (tx.type === 'purchase') {
                    verwendungszweck = `${tx.package_name || 'SNAP-Paket'}`;
                  } else if (tx.type === 'referral_bonus') {
                    verwendungszweck = 'Empfehlungsbonus';
                  } else if (tx.type === 'welcome_bonus') {
                    verwendungszweck = 'Willkommensbonus';
                  } else if (tx.type === 'admin_credit') {
                    verwendungszweck = `Admin-Gutschrift${tx.admin_email ? ` (${tx.admin_email})` : ''}`;
                  } else {
                    verwendungszweck = tx.reason || 'Transaktion';
                  }
                  
                  return (
                    <div
                      key={tx.id || idx}
                      className="px-6 py-3 hover:bg-white/5 transition-colors"
                    >
                      {/* Desktop Layout - Alles in EINER Zeile */}
                      <div className="hidden md:grid md:grid-cols-12 gap-3 items-center text-sm">
                        <div className="col-span-2 text-white font-mono">{dateStr}</div>
                        <div className="col-span-1 text-white/80 font-mono">{timeStr}</div>
                        <div className="col-span-5 text-white">{verwendungszweck}</div>
                        <div className={`col-span-2 text-right font-mono font-bold ${isExpense ? 'text-red-400' : 'text-[#10B981]'}`}>
                          {Math.round((tx.amount_snaps || 0) * 100) / 100}
                        </div>
                        <div className="col-span-2 text-right text-white font-mono font-bold">
                          {tx.amount_eur ? `€${tx.amount_eur.toFixed(2)}` : `€${(Math.abs(tx.amount_snaps || 0) / snapsPerEuro).toFixed(2)}`}
                        </div>
                      </div>

                      {/* Mobile Layout - Auch in einer Zeile */}
                      <div className="md:hidden">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-white/60 font-mono">{dateStr} · {timeStr}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-baseline gap-2">
                            <span className={`font-mono font-bold ${isExpense ? 'text-red-400' : 'text-[#10B981]'}`}>
                              {Math.round((tx.amount_snaps || 0) * 100) / 100} SNAPs
                            </span>
                            <span className="text-white/60 text-sm">·</span>
                            <span className="text-white text-sm">{verwendungszweck}</span>
                          </div>
                          <span className="text-white font-mono font-bold ml-2">
                            {tx.amount_eur ? `€${tx.amount_eur.toFixed(2)}` : `€${(Math.abs(tx.amount_snaps || 0) / snapsPerEuro).toFixed(2)}`}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer mit detaillierter Übersicht */}
              <div className="px-6 py-6 bg-white/5 border-t-2 border-white/20">
                {/* Desktop */}
                <div className="hidden md:block">
                  <div className="grid grid-cols-2 gap-8 mb-4">
                    {/* Einnahmen */}
                    <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                      <p className="text-white/70 text-sm mb-2">Käufe</p>
                      <div className="flex justify-between items-baseline">
                        <p className="text-[#10B981] font-mono font-bold text-2xl">
                          {Math.round(transactionHistory.filter(tx => tx.source === 'payment').reduce((sum, tx) => sum + (tx.amount_snaps || 0), 0) * 100) / 100} SNAPs
                        </p>
                        <p className="text-white/80 font-mono font-bold text-lg">
                          €{transactionHistory.filter(tx => tx.source === 'payment').reduce((sum, tx) => sum + (tx.amount_eur || 0), 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                    
                    {/* Ausgaben - Placeholder (wird später mit echten Daten gefüllt) */}
                    <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-white/70 text-sm mb-2">Ausgaben (Bilder, Training, etc.)</p>
                      <div className="flex justify-between items-baseline">
                        <p className="text-red-400 font-mono font-bold text-2xl">
                          {userSnaps > 0 ? Math.round((transactionHistory.reduce((sum, tx) => sum + (tx.amount_snaps || 0), 0) - userSnaps) * 100) / 100 : '0'} SNAPs
                        </p>
                        <p className="text-white/40 text-sm">
                          (berechnet)
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Aktueller Kontostand */}
                  <div className="p-4 bg-white/10 border-2 border-white/20 rounded-lg">
                    <div className="flex justify-between items-baseline">
                      <p className="text-white font-bold text-lg">Aktueller Kontostand</p>
                      <p className="text-[#10B981] font-mono font-bold text-3xl">{Math.round(userSnaps * 100) / 100} SNAPs</p>
                    </div>
                  </div>
                </div>

                {/* Mobile */}
                <div className="md:hidden space-y-3">
                  <div className="p-3 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
                    <p className="text-white/70 text-xs mb-1">Käufe</p>
                    <div className="flex justify-between items-baseline">
                      <p className="text-[#10B981] font-mono font-bold text-lg">
                        {Math.round(transactionHistory.filter(tx => tx.source === 'payment').reduce((sum, tx) => sum + (tx.amount_snaps || 0), 0) * 100) / 100} SNAPs
                      </p>
                      <p className="text-white/80 font-mono">
                        €{transactionHistory.filter(tx => tx.source === 'payment').reduce((sum, tx) => sum + (tx.amount_eur || 0), 0).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <p className="text-white/70 text-xs mb-1">Ausgaben (berechnet)</p>
                    <p className="text-red-400 font-mono font-bold text-lg">
                      {userSnaps > 0 ? Math.round((transactionHistory.reduce((sum, tx) => sum + (tx.amount_snaps || 0), 0) - userSnaps) * 100) / 100 : '0'} SNAPs
                    </p>
                  </div>
                  
                  <div className="p-3 bg-white/10 border-2 border-white/20 rounded-lg">
                    <p className="text-white/70 text-xs mb-1">Kontostand</p>
                    <p className="text-[#10B981] font-mono font-bold text-2xl">{Math.round(userSnaps * 100) / 100} SNAPs</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Warning Modal für SNAP-Pakete ohne Avatar */}
        {showWarningModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="snap-warning-modal">
            <div className="bg-black border-2 border-yellow-500/50 rounded-2xl max-w-md w-full p-6 grain-texture">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">⚠️ Bitte erstelle zuerst einen Avatar</h3>
                  <p className="text-white/80 text-sm leading-relaxed">
                    Dafür benötigst du ein <span className="font-bold text-[#10B981]">Starter-, Standard- oder Premium-Paket</span>.
                  </p>
                  <p className="text-white/60 text-sm mt-3">
                    Diese Zusatzpakete sind ausschließlich für SNAPSHOTs (Bildgenerierungen) gedacht.
                  </p>
                  <p className="text-white/60 text-sm mt-2">
                    Sobald ein Avatar aktiv ist, werden diese drei Pakete automatisch freigeschaltet.
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowWarningModal(false)}
                className="w-full py-3 px-6 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all"
                data-testid="warning-modal-close"
              >
                Verstanden
              </button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {showSuccessMessage && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
            <div className="bg-[#10B981] text-black p-4 rounded-xl shadow-2xl flex items-center gap-3 glow-green">
              <CheckCircle className="w-6 h-6 flex-shrink-0" />
              <p className="font-bold">SNAPs erfolgreich aufgeladen!</p>
            </div>
          </div>
        )}

        {/* Link Generated Toast */}
        {showLinkToast && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-full mx-4">
            <div className="bg-[#10B981] text-black p-4 rounded-xl shadow-2xl flex items-center gap-3 glow-green">
              <CheckCircle className="w-6 h-6 flex-shrink-0" />
              <p className="font-bold">✅ Neuer Einladungslink wurde generiert!</p>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedPackage && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => !isProcessing && setShowModal(false)}
          >
            <div 
              className="relative w-full max-w-md bg-black border-2 border-[#10B981]/30 rounded-2xl p-6 sm:p-8 glow-green-soft"
              onClick={(e) => e.stopPropagation()}
            >
              
              <button
                onClick={() => !isProcessing && setShowModal(false)}
                disabled={isProcessing}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-lg transition-all disabled:opacity-50"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center glow-green">
                  <selectedPackage.icon className="w-10 h-10 text-black" />
                </div>

                <h3 className="text-2xl font-bold mb-2">Paket bestätigen</h3>
                <p className="text-white/70 mb-6">
                  Möchtest du <span className="text-[#10B981] font-bold">{selectedPackage.name}</span> wirklich kaufen?
                </p>

                <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                  {selectedPackage.avatarCount > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/70">Avatare:</span>
                      <span className="text-[#10B981] font-bold text-xl">{selectedPackage.avatarCount}</span>
                    </div>
                  )}
                  {selectedPackage.imageCount > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/70">Bilder:</span>
                      <span className="text-white font-bold text-lg">{selectedPackage.imageCount}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Preis:</span>
                    <span className="text-white font-bold text-xl">€{selectedPackage.price.toFixed(2)}</span>
                  </div>
                  <div className="text-right mt-1">
                    <span className="text-white/40 text-xs">inkl. 19 % MwSt.</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowModal(false)}
                    disabled={isProcessing}
                    className="flex-1 py-3 px-6 border-2 border-white/30 text-white rounded-xl hover:bg-white/5 transition-all font-medium disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleConfirmPurchase}
                    disabled={isProcessing}
                    className="flex-1 py-3 px-6 bg-[#10B981] text-black rounded-xl hover:bg-[#059669] transition-all font-bold glow-green pulse-glow disabled:opacity-50"
                  >
                    {isProcessing ? 'Weiterleitung...' : 'Weiter zum Check-in'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
